import config from '../config';
import { customerNeedsFollowUpCall } from '../pages/customers/voiceCallBatchStatus';

export const VOICE_CALLS_ENABLED = config.voiceCallServerEnabled;

export function isNameNA(name) {
  if (!name) return true;
  const lower = String(name).trim().toLowerCase();
  return ['n/a', 'na', 'unknown', 'temporary user', ''].includes(lower);
}

/** Ops /call-customer expects 91XXXXXXXXXX digits. */
export function normalizeOpsCallPhone(raw) {
  let digits = String(raw || '').replace(/\D/g, '');
  if (digits.length === 10) digits = `91${digits}`;
  return digits;
}

/** Voice bot /call expects 10-digit local number. */
export function normalizeBotCallPhone(raw) {
  let phone = String(raw || '').replace(/\D/g, '');
  if (phone.startsWith('91') && phone.length === 12) phone = phone.slice(2);
  return phone;
}

export function memberToCallCustomer(member) {
  const raw = member?.phone_e164 || member?.phone || '';
  const digits = String(raw).replace(/\D/g, '');
  return {
    id: member?.customer_id ?? null,
    phone: digits.length >= 10 ? digits : raw,
    name: member?.name || member?.greeting_name || member?.business_name || '',
    call_summary: member?.call_summary,
    remarks: member?.remarks,
  };
}

export async function saveCustomerNameIfNeeded(customer, callName, apiBase = config.customerApiBase) {
  if (!customer?.id) return false;
  const trimmed = callName?.trim();
  if (!trimmed || isNameNA(trimmed)) return false;

  const existingName = customer.name?.trim() || '';
  if (!isNameNA(existingName) && existingName.toLowerCase() === trimmed.toLowerCase()) {
    return false;
  }

  const detailsResp = await fetch(`${apiBase}/customers/${customer.id}/details`);
  if (!detailsResp.ok) {
    throw new Error('Failed to load customer details before saving name');
  }
  const details = await detailsResp.json();
  const c = details.customer;

  const payload = {
    name: trimmed,
    preferred_language: c.preferred_language || '',
    primary_address_id: c.primary_address_id ?? null,
    business_name: c.business_name || '',
    business_category: c.business_category || '',
    matched_category: c.matched_category || '',
    domain: c.domain || '',
    remarks: c.remarks || '',
    star: !!c.starred,
    discount_percentage: c.discount_percentage ?? null,
  };

  const response = await fetch(`${apiBase}/customers/${customer.id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to save customer name');
  }

  return true;
}

export async function fetchActiveCustomerSims() {
  const token = localStorage.getItem('token') || '';
  const res = await fetch(`${config.opsApiBase}/call-server/active-sims?role=customer`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json();
  if (!data.success || !Array.isArray(data.sims)) return [];
  return data.sims;
}

export async function fetchCustomerExtraPhones(customerId, apiBase = config.customerApiBase) {
  if (!customerId) return [];
  const res = await fetch(`${apiBase}/customers/${customerId}/phones`);
  if (!res.ok) return [];
  const data = await res.json().catch(() => ({}));
  return Array.isArray(data.phones) ? data.phones : [];
}

/** Main number first, then saved extras. Duplicate last-10 numbers are dropped. */
export function buildCustomerDialOptions(primaryPhone, extraPhones) {
  const options = [];
  const seen = new Set();
  const add = (phone, label, remark, id) => {
    const digits = String(phone || '').replace(/\D/g, '');
    if (digits.length < 10) return;
    const key = digits.slice(-10);
    if (seen.has(key)) return;
    seen.add(key);
    options.push({
      id: id || key,
      phone: digits,
      label: label || '',
      remark: remark || '',
    });
  };
  add(primaryPhone, 'Main', '', 'main');
  (extraPhones || []).forEach((entry) => {
    add(entry.phone || entry.phone_number, entry.label, entry.remark, entry.id);
  });
  return options;
}

export async function placeOpsCustomerCall({ customer, callName, simNumber, dialPhone }) {
  const digits = normalizeOpsCallPhone(dialPhone || customer?.phone);
  if (!digits) throw new Error('Customer has no phone number');

  if (customer?.id) {
    await saveCustomerNameIfNeeded(customer, callName);
  }

  const token = localStorage.getItem('token') || '';
  const response = await fetch(`${config.opsApiBase}/call-customer`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      customer_phone: digits,
      customer_id: customer?.id || null,
      sim_number: simNumber || null,
    }),
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.detail || data.error || 'Failed to place call');
  }
  return data;
}

export async function placeBotCustomerCall({ customer, callName, dialPhone }) {
  if (!VOICE_CALLS_ENABLED) {
    throw new Error('Voice call server is disabled');
  }

  const phone = normalizeBotCallPhone(dialPhone || customer?.phone);
  if (!phone || phone.length < 10) throw new Error('Customer has no phone number');

  const companyName = callName && !isNameNA(callName) ? callName.trim() : '';

  if (customer?.id) {
    await saveCustomerNameIfNeeded(customer, callName);
  }

  const response = await fetch(`${config.voiceCallServerBase}/call`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      phone_number: phone,
      company_name: companyName,
      customer_id: customer?.id || null,
      call_type: customerNeedsFollowUpCall(customer) ? 'followup' : 'first_call',
      call_origin: 'customer',
    }),
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || data.message || 'Failed to trigger call');
  }
  return data;
}
